<script setup lang="ts" generic="DataT">
import { computed } from 'vue'
import type { Table } from '../../../../types'
import Button from '../Button.vue'
import { SortDirection } from '../../../enums/sort-direction'

interface Props {
  cell: Table.Cell<DataT>
  rowIndex: number
  cellIndex: number
  sortCellIndex?: number
  sortRowIndex?: number
  sortIcons: Table.SortIconsOptions
  sortDirection?: SortDirection
  isTableSortable: boolean
  className?: Table.ClassName
  sortableClassName?: Table.ClassName
  sortingClassName?: Table.ClassName
  wrapperClassname?: Table.ClassName
  cellClassname?: Table.ClassName
}

interface Emits {
  (event: 'sort', value: {
    rowIndex: number
    cellIndex: number
    direction?: SortDirection
  }): void
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const isSortable = computed<boolean>(() => {
  if (!props.isTableSortable || !props.cell?.sort) {
    return false
  }

  return props.cell.sort
})

const isSorting = computed<boolean>(() => {
  if (!props.cell?.sort) {
    return false
  }

  return props.rowIndex === props.sortRowIndex && props.cellIndex === props.sortCellIndex
})

const sortLabel = computed<string>(() => {
  switch (props.sortDirection) {
    case SortDirection.Ascending:
      return 'Sorting ascending'
    case SortDirection.Descending:
      return 'Sorting descending'
    default:
      return 'Sort'
  }
})

const currentIcon = computed<string>(() => {
  if (!isSorting.value) {
    return props.sortIcons.default
  }

  switch (props.sortDirection) {
    case SortDirection.Ascending:
      return props.sortIcons.asc
    case SortDirection.Descending:
      return props.sortIcons.desc
    default:
      return props.sortIcons.default
  }
})

const handleSortButton = () => {
  if (!isSortable.value) {
    return
  }

  const { sortDirection } = props
  const isPreviouslyDescending = !sortDirection || sortDirection === SortDirection.Descending

  const nextDirection: SortDirection = isPreviouslyDescending ? SortDirection.Ascending : SortDirection.Descending

  emit('sort', {
    rowIndex: props.rowIndex,
    cellIndex: props.cellIndex,
    direction: nextDirection,
  })
}

const wrapperProps = computed<Record<string, unknown>>(() => {
  if (!isSortable.value) {
    return {}
  }

  return {
    variant: 'clear',
    title: sortLabel.value,
    appendedIcon: currentIcon.value,
    onClick: handleSortButton,
  }
})

const classNames = computed<Table.ClassName[]>(() => {
  const { className, sortableClassName, sortingClassName } = props
  const classNames = []

  if (className) {
    classNames.push(className)
  }

  if (isSortable.value && sortableClassName) {
    classNames.push(sortableClassName)
  }

  if (isSorting.value && sortingClassName) {
    classNames.push(sortingClassName)
  }

  return classNames
})
</script>

<template>
  <th
    :class="classNames"
  >
    <Component
      :is="isSortable ? Button : 'div'"
      v-bind="wrapperProps"
      :class="[wrapperClassname, cellClassname]"
    >
      <span>{{ cell.content }}</span>
    </Component>
  </th>
</template>
